import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  Autocomplete,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  type SxProps,
  type Theme,
} from '@mui/material';
import {
  Inventory,
  Restaurant,
  People,
  Star,
  Search,
  CalendarToday,
  ChecklistRtl,
  Settings,
  CalendarMonth,
  EventAvailable,
  ExitToApp,
  Info,
  DarkMode,
  LightMode,
  Event as EventIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { BottomNavigation } from '../layout/BottomNavigation';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useLimits } from '../hooks/useLimits';
import { UpgradePrompt } from '../components/subscription/UpgradePrompt';
import { TrialStatusBanner } from '../components/subscription/TrialStatusBanner';
import { LimitReachedDialog } from '../components/subscription/LimitReachedDialog';
import { EventsDialog } from '../components/eventsDialog';
import { getUserEvents, Event } from '../services/eventsService';
import { getUserRecipes, Recipe } from '../services/recipesService';
import { getUserGuests } from '../services/guestsService';
import { useInventory } from '../hooks/useInventory';
import { useWishlist } from '../hooks/useWishlist';
import { useSafeArea } from '../hooks/useSafeArea';
import { TrialOfferDialog } from '../components/subscription/TrialOfferDialog';
import { useTodoList } from '../hooks/useTodoList';
import { CheckBoxOutlineBlank } from '@mui/icons-material';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { BOTTOM_NAV_HEIGHT, BOTTOM_NAV_SPACING } from '../layout/BottomNavigation';

export const Dashboard: React.FC = () => {
  const { theme, isDark, toggleTheme } = useTheme();
  const { logout, hostlyUser, user } = useAuth();

  useEffect(() => {
    if (user?.uid && window.clarity) {
      window.clarity('identify', user.uid, undefined, undefined, hostlyUser?.name || undefined);
    }
  }, [user?.uid, hostlyUser?.name]);

  const { plan, subscription, isPremium, isPro } = useSubscription();
  const {
    eventsRemaining,
    checkEventLimit,
    refreshLimits
  } = useLimits();
  const navigate = useNavigate();
  const { top, bottom } = useSafeArea();

  const { items: inventoryItems } = useInventory();
  const { items: wishlistItems } = useWishlist();
  const { todosForDate, toggleTodo } = useTodoList();

  const [limitDialog, setLimitDialog] = useState({ open: false, message: '' });
  const [showEventsDialog, setShowEventsDialog] = useState(false);
  const [nextEvent, setNextEvent] = useState<Event | null>(null);
  const [futureEventsCount, setFutureEventsCount] = useState(0);
  const [mostUsedRecipe, setMostUsedRecipe] = useState<Recipe | null>(null);
  const [mostUsedItem, setMostUsedItem] = useState<any>(null);
  const [allRecipes, setAllRecipes] = useState<Recipe[]>([]);
  const [featuredItemId, setFeaturedItemId] = useState<string | null>(null);
  const [featuredRecipeId, setFeaturedRecipeId] = useState<string | null>(null);
  const [pickItemOpen, setPickItemOpen] = useState(false);
  const [pickRecipeOpen, setPickRecipeOpen] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [searchOptions, setSearchOptions] = useState<any[]>([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });
  const [showTrialOffer, setShowTrialOffer] = useState(false);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const menu = document.getElementById('profile-menu');
      const target = event.target as HTMLElement;

      if (menu && menu.style.display === 'block' && !menu.contains(target) && !target.closest('[data-profile-button]')) {
        menu.style.display = 'none';
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Estilos com tipagem explícita
  const containerStyles: SxProps<Theme> = {
    paddingTop: `${Math.max(top, 5) + 5}px`,
    paddingBottom: `${bottom + BOTTOM_NAV_HEIGHT + BOTTOM_NAV_SPACING + 24}px`,
    paddingLeft: 2,
    paddingRight: 2,
    maxWidth: { xs: '100%', md: '900px', lg: '1200px' }, // Mobile: 100%, Tablet: 900px, Desktop: 1200px
    width: '100%',
    margin: '0 auto',
    minHeight: '100vh',
  };

  const headerBoxStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    marginBottom: 2,
  };

  const avatarStyles: SxProps<Theme> = {
    width: 40,
    height: 40,
    backgroundColor: theme.colors.secondary,
  };

  const nameTextStyles: SxProps<Theme> = {
    color: theme.colors.secondary,
    fontWeight: 'bold',
    fontSize: '1rem',
    lineHeight: 1,
  };

  const lastNameTextStyles: SxProps<Theme> = {
    color: theme.colors.secondary,
    fontSize: '0.65rem',
    fontWeight: 600,
    letterSpacing: '0.1em',
    lineHeight: 1,
  };

  const iconButtonStyles: SxProps<Theme> = {
    width: 32,
    height: 32,
    backgroundColor: theme.colors.secondary,
    color: theme.colors.text.onSecondary,
    '&:hover': {
      backgroundColor: theme.colors.secondary,
      opacity: 0.8,
    },
  };

  const profileMenuStyles: SxProps<Theme> = {
    display: 'none',
    position: 'fixed',
    top: `${Math.max(top, 5) + 50}px`,
    right: '16px',
    width: 160,
    backgroundColor: theme.colors.background,
    borderRadius: theme.radii.medium,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    zIndex: 1300,
    overflow: 'hidden',
    border: `1px solid ${theme.colors.border}`,
  };

  const menuItemStyles: SxProps<Theme> = {
    p: 1.5,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    cursor: 'pointer',
    backgroundColor: 'transparent',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: theme.colors.card,
    },
  };

  const menuItemTextStyles: SxProps<Theme> = {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.colors.text.primary,
    whiteSpace: 'nowrap',
  };

  const shortcutCardStyles: SxProps<Theme> = {
    backgroundColor: theme.colors.primary,
    color: theme.colors.text.contrast,
    borderRadius: theme.radii.medium,
    cursor: 'pointer',
    transition: 'transform 0.1s ease-out, box-shadow 0.1s ease-out',
    '&:hover': {
      transform: 'scale(1.02)',
      boxShadow: theme.shadows.medium,
    },
    '&:active': {
      transform: 'scale(0.98)',
      transition: 'transform 0.05s ease-out',
    },
    border: 'none',
    boxShadow: theme.shadows.small,
  };

  const eventCardStyles: SxProps<Theme> = {
    marginBottom: 2,
    borderRadius: theme.radii.medium,
    backgroundColor: theme.colors.card,
    border: 'none',
    boxShadow: theme.shadows.small,
  };

  const wishlistCardStyles: SxProps<Theme> = {
    borderRadius: theme.radii.large,
    overflow: 'hidden',
    border: 'none',
    boxShadow: theme.shadows.small,
    position: 'relative',
    aspectRatio: '1.3 / 1', // Proporção responsiva ao invés de altura fixa
    width: '100%',
    cursor: 'pointer',
    transition: 'transform 0.1s ease-out, box-shadow 0.1s ease-out',
    '&:hover': {
      transform: 'scale(1.02)',
      boxShadow: theme.shadows.medium,
    },
    '&:active': {
      transform: 'scale(0.98)',
      transition: 'transform 0.05s ease-out',
    },
  };

  const wishlistLabelStyles: SxProps<Theme> = {
    position: 'absolute',
    bottom: 6,
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: theme.colors.text.contrast,
    px: 1.5,
    py: 0.5,
    borderRadius: theme.radii.medium,
    boxShadow: theme.shadows.medium,
    width: 'auto',
    maxWidth: '90%',
  };

  const shortcuts = [
    { icon: <Inventory />, title: 'Acervo', onClick: () => navigate('/inventory') },
    {
      icon: <Restaurant />,
      title: 'Receitas',
      onClick: () => {
        console.log('Navegando para /recipes');
        navigate('/recipes');
      }
    },
    { icon: <People />, title: 'Convidados', onClick: () => navigate('/guests') },
    { icon: <CalendarToday />, title: 'Calendário', onClick: () => navigate('/calendar') },
    { icon: <ChecklistRtl />, title: 'Tarefas', onClick: () => navigate('/todo-list') },
    { icon: <Star />, title: 'Wishlist', onClick: () => navigate('/wishlist') },
  ];

  const getInitials = () => {
    const firstName = hostlyUser?.name || '';
    const lastName = hostlyUser?.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getDisplayName = () => {
    return hostlyUser?.name || 'Usuário';
  };

  const getDisplayLastName = () => {
    return hostlyUser?.lastName?.toUpperCase() || 'CADASTRADO';
  };

  const getPlanLabel = () => {
    if (subscription?.status === 'trialing') {
      if (subscription.plan === 'pro') return 'Trial Pro';
      if (subscription.plan === 'premium') return 'Trial Basic';
      return 'Trial';
    }
    if (isPro) return 'Pro';
    if (isPremium) return 'Basic';
    return 'Free';
  };

  const getPlanColor = () => {
    if (subscription?.status === 'trialing') return '#8B5CF6';
    if (isPro) return '#4caf50';
    if (isPremium) return '#ff9800';
    return theme.colors.text.secondary;
  };

  const handleAddEvent = async () => {
    if (!user?.uid) {
      navigate('/login');
      return;
    }

    try {
      const limitCheck = await checkEventLimit();

      if (!limitCheck.allowed) {
        setLimitDialog({
          open: true,
          message: limitCheck.message || 'Limite de eventos atingido'
        });
        return;
      }

      setShowEventsDialog(true);
    } catch (error) {
      console.error('Erro ao verificar limite:', error);
    }
  };

  const handleEventCreated = async () => {
    await refreshLimits();
    setShowEventsDialog(false);
  };

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  // Verificar se deve mostrar oferta de trial (apenas uma vez)
  useEffect(() => {
    const checkTrialOffer = async () => {
      if (!user?.uid) return;

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data();

        if (userData?.trialOfferSeen) return;

        const subDoc = await getDoc(doc(db, 'users', user.uid, 'subscription', 'current'));
        const subData = subDoc.data();

        if (subData?.plan === 'free' && subData?.status === 'active' && !subData?.hadTrial) {
          setShowTrialOffer(true);
        }
      } catch (error) {
        return;
      }
    };

    checkTrialOffer();
  }, [user?.uid]);

  const handleAcceptTrial = async () => {
    if (!user?.uid) return;

    try {
      const trialEndDate = new Date();
      trialEndDate.setDate(trialEndDate.getDate() + 30);

      const subscriptionRef = doc(db, 'users', user.uid, 'subscription', 'current');
      await updateDoc(subscriptionRef, {
        plan: 'premium',
        status: 'trialing',
        trialEnd: trialEndDate,
        hadTrial: true,
        updatedAt: new Date(),
      });

      await updateDoc(doc(db, 'users', user.uid), {
        trialOfferSeen: true,
      });

      setShowTrialOffer(false);
      setSnackbar({
        open: true,
        message: 'Trial de 30 dias ativado com sucesso',
        severity: 'success',
      });

      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erro ao ativar trial. Tente novamente.',
        severity: 'error',
      });
    }
  };

  const handleDeclineTrial = async () => {
    if (!user?.uid) return;

    try {
      await updateDoc(doc(db, 'users', user.uid), {
        trialOfferSeen: true,
      });

      // Se a subscription tinha entrado em Trial Basic por race condition
      // (getSubscription fallback antigo), normaliza para Free/active.
      const subRef = doc(db, 'users', user.uid, 'subscription', 'current');
      const subSnap = await getDoc(subRef);
      const subData = subSnap.data();
      if (subData?.status === 'trialing' && !subData?.hadTrial) {
        await updateDoc(subRef, {
          plan: 'free',
          status: 'active',
          updatedAt: new Date(),
        });
      }

      setShowTrialOffer(false);
    } catch (error) {
      setShowTrialOffer(false);
    }
  };

  // Carregar opções de busca
  useEffect(() => {
    const loadSearchOptions = async () => {
      if (!user?.uid) return;

      setLoadingSearch(true);
      try {
        const options: any[] = [];

        // Páginas estáticas
        const pages = [
          { id: 'inventory-page', title: 'Acervo', type: 'page', path: '/inventory', icon: 'inventory' },
          { id: 'recipes-page', title: 'Receitas', type: 'page', path: '/recipes', icon: 'restaurant' },
          { id: 'guests-page', title: 'Convidados', type: 'page', path: '/guests', icon: 'person' },
          { id: 'calendar-page', title: 'Calendário', type: 'page', path: '/calendar', icon: 'event' },
          { id: 'todos-page', title: 'Tarefas', type: 'page', path: '/todo-list', icon: 'checklist' },
          { id: 'wishlist-page', title: 'Wishlist', type: 'page', path: '/wishlist', icon: 'star' },
          { id: 'events-page', title: 'Eventos', type: 'page', path: '/events', icon: 'event' },
        ];
        options.push(...pages);

        // Buscar eventos do serviço
        const events = await getUserEvents(user.uid);
        events.forEach(event => {
          options.push({
            id: `event-${event.id}`,
            title: event.title,
            subtitle: new Date(event.date).toLocaleDateString('pt-BR'),
            type: 'event',
            path: `/events/${event.id}`,
            icon: 'event',
          });
        });

        // Buscar convidados do serviço
        const guests = await getUserGuests(user.uid);
        guests.forEach(guest => {
          options.push({
            id: `guest-${guest.id}`,
            title: guest.name,
            subtitle: guest.email || guest.phone,
            type: 'guest',
            path: `/guests/${guest.id}`,
            icon: 'person',
          });
        });

        // Buscar receitas do serviço
        const recipes = await getUserRecipes(user.uid);
        recipes.forEach(recipe => {
          options.push({
            id: `recipe-${recipe.id}`,
            title: recipe.title,
            subtitle: recipe.description,
            type: 'recipe',
            path: `/recipes/${recipe.id}`,
            icon: 'restaurant',
          });
        });

        // Buscar inventário do hook
        inventoryItems.forEach(item => {
          options.push({
            id: `inventory-${item.id}`,
            title: item.itemName,
            subtitle: `${item.category || ''} - ${item.brand || ''}`.trim(),
            type: 'inventory',
            path: `/inventory/${item.id}`,
            icon: 'inventory',
          });
        });

        // Buscar wishlist do hook
        wishlistItems.forEach(item => {
          options.push({
            id: `wishlist-${item.id}`,
            title: item.title,
            subtitle: item.store,
            type: 'wishlist',
            path: '/wishlist',
            icon: 'star',
          });
        });

        setSearchOptions(options);
      } catch (error) {
        console.error('Erro ao carregar opções de busca:', error);
      } finally {
        setLoadingSearch(false);
      }
    };

    loadSearchOptions();
  }, [user?.uid, inventoryItems, wishlistItems]);

  const getIcon = (iconName: string) => {
    const icons: any = {
      event: <EventIcon fontSize="small" />,
      person: <People fontSize="small" />,
      restaurant: <Restaurant fontSize="small" />,
      inventory: <Inventory fontSize="small" />,
      star: <Star fontSize="small" />,
      checklist: <ChecklistRtl fontSize="small" />,
    };
    return icons[iconName] || <Search fontSize="small" />;
  };
  useEffect(() => {
    const loadDashboardData = async () => {
      if (!user?.uid) {
        setLoadingData(false);
        return;
      }

      try {
        setLoadingData(true);

        // Carregar próximo evento
        const events = await getUserEvents(user.uid);
        const now = new Date();
        now.setHours(0, 0, 0, 0);

        const futureEvents = events
          .filter(event => {
            const eventDate = new Date(event.date);
            eventDate.setHours(0, 0, 0, 0);
            return eventDate >= now;
          })
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        setFutureEventsCount(futureEvents.length);

        if (futureEvents.length > 0) {
          setNextEvent(futureEvents[0]);
        }

        // Carregar receita mais usada
        const recipes = await getUserRecipes(user.uid);
        setAllRecipes(recipes);
        if (recipes.length > 0) {
          const sortedRecipes = recipes.sort((a, b) => (b.eventsUsed || 0) - (a.eventsUsed || 0));
          setMostUsedRecipe(sortedRecipes[0]);
        }

        // Item mais usado (do inventário)
        if (inventoryItems.length > 0) {
          // Assumindo que você vai adicionar um campo 'eventsUsed' no inventário
          const sortedItems = [...inventoryItems].sort((a, b) => {
            const aUsed = (a as any).eventsUsed || 0;
            const bUsed = (b as any).eventsUsed || 0;
            return bUsed - aUsed;
          });
          setMostUsedItem(sortedItems[0]);
        }

        // Destaques fixados pelo usuário (se houver)
        try {
          const userSnap = await getDoc(doc(db, 'users', user.uid));
          const data = userSnap.data();
          setFeaturedItemId(data?.featuredInventoryId || null);
          setFeaturedRecipeId(data?.featuredRecipeId || null);
        } catch { /* ignora */ }
      } catch (error) {
        console.error('Erro ao carregar dados do dashboard:', error);
      } finally {
        setLoadingData(false);
      }
    };

    loadDashboardData();
  }, [user?.uid, inventoryItems]);

  // Destaque: item/receita fixado pelo usuário; senão o mais usado
  const featuredItem = (featuredItemId && inventoryItems.find((i: any) => i.id === featuredItemId)) || mostUsedItem;
  const featuredRecipe = (featuredRecipeId && allRecipes.find(r => r.id === featuredRecipeId)) || mostUsedRecipe;

  const saveFeatured = async (field: 'featuredInventoryId' | 'featuredRecipeId', value: string | null) => {
    if (!user?.uid) return;
    if (field === 'featuredInventoryId') setFeaturedItemId(value);
    else setFeaturedRecipeId(value);
    try {
      await updateDoc(doc(db, 'users', user.uid), { [field]: value });
    } catch { /* ignora */ }
  };

  return (
    <Box sx={containerStyles}>
      {/* Header */}
      <Box sx={headerBoxStyles}>
        <Avatar
          src={hostlyUser?.profilePictureUrl || undefined}
          sx={avatarStyles}
        >
          {!hostlyUser?.profilePictureUrl && getInitials()}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={nameTextStyles}>
            {getDisplayName()}
          </Typography>
          <Typography sx={lastNameTextStyles}>
            {getDisplayLastName()}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            onClick={toggleTheme}
            aria-label={isDark ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
            sx={iconButtonStyles}
          >
            {isDark ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
          </IconButton>
          <IconButton
            onClick={() => navigate('/general-settings')}
            aria-label="Configurações gerais"
            sx={iconButtonStyles}
          >
            <Info fontSize="small" />
          </IconButton>
          <IconButton
            data-profile-button
            aria-label="Menu do perfil"
            onClick={() => {
              const menu = document.getElementById('profile-menu');
              if (menu) {
                menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
              }
            }}
            sx={iconButtonStyles}
          >
            <Avatar
              src="/profileIcons.png"
              sx={{
                width: 28,
                height: 28,
                backgroundColor: 'transparent',
              }}
            />
          </IconButton>
        </Box>
      </Box>

      {/* Profile Menu - Outside of header for proper positioning */}
      <Box id="profile-menu" sx={profileMenuStyles}>
        <Box
          onClick={() => {
            document.getElementById('profile-menu')!.style.display = 'none';
            navigate('/settings');
          }}
          sx={menuItemStyles}
        >
          <Settings sx={{ color: theme.colors.text.primary, fontSize: 20 }} />
          <Typography sx={menuItemTextStyles}>
            Configurações
          </Typography>
        </Box>
        <Box
          onClick={() => {
            document.getElementById('profile-menu')!.style.display = 'none';
            logout();
          }}
          sx={{
            ...menuItemStyles,
            borderTop: `1px solid ${theme.colors.border}`,
          }}
        >
          <ExitToApp sx={{ color: theme.colors.text.primary, fontSize: 20 }} />
          <Typography sx={menuItemTextStyles}>
            Sair
          </Typography>
        </Box>
      </Box>

      {/* Trial Status Banner */}
      <TrialStatusBanner />

      {/* Plan Badge */}
      <Box
        onClick={() => navigate('/pricing')}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          mb: 1,
          cursor: 'pointer',
        }}
      >
        <Chip
          label={`Plano ${getPlanLabel()}`}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.65rem',
            fontWeight: 'bold',
            backgroundColor: getPlanColor(),
            color: 'white',
            '&:hover': {
              opacity: 0.9,
            },
          }}
        />
      </Box>

      {/* Search Autocomplete */}
      <Box sx={{ marginBottom: 2 }}>
        <Autocomplete
          freeSolo
          options={searchOptions}
          loading={loadingSearch}
          getOptionLabel={(option: any) => option.title || ''}
          groupBy={(option: any) => {
            const labels: any = {
              page: 'Páginas',
              event: 'Eventos',
              guest: 'Convidados',
              recipe: 'Receitas',
              inventory: 'Acervo',
              wishlist: 'Wishlist',
            };
            return labels[option.type] || 'Outros';
          }}
          onChange={(_, value: any) => {
            if (value && value.path) {
              navigate(value.path);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Buscar eventos, convidados, receitas..."
              variant="outlined"
              size="small"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start" sx={{ ml: 0 }}>
                    <IconButton
                      sx={{
                        backgroundColor: theme.colors.primary,
                        color: theme.colors.text.contrast,
                        width: 36,
                        height: 36,
                        p: 0,
                        '&:hover': {
                          backgroundColor: theme.colors.primary,
                          opacity: 0.8,
                        },
                      }}
                    >
                      <Search fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {loadingSearch ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
                sx: {
                  borderRadius: theme.radii.pill,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.colors.primary,
                    borderRadius: theme.radii.pill,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.colors.primary,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.colors.primary,
                    borderWidth: 1,
                  },
                },
              }}
            />
          )}
          renderOption={(props, option: any) => (
            <ListItem {...props} key={option.id}>
              <ListItemIcon sx={{ minWidth: 40, color: theme.colors.primary }}>
                {getIcon(option.icon)}
              </ListItemIcon>
              <ListItemText
                primary={option.title}
                secondary={option.subtitle}
                primaryTypographyProps={{
                  sx: { fontWeight: 500 },
                }}
                secondaryTypographyProps={{
                  sx: {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
            </ListItem>
          )}
        />
      </Box>

      {/* Shortcuts Grid */}
      <Grid container spacing={1.5} sx={{ marginBottom: 3 }}>
        {shortcuts.map((shortcut, index) => (
          <Grid key={index} size={{ xs: 4, md: 2 }}>
            <Card onClick={shortcut.onClick} sx={shortcutCardStyles}>
              <CardContent sx={{
                textAlign: 'center',
                padding: 2,
                '&:last-child': { paddingBottom: 2 }
              }}>
                <Box sx={{ fontSize: '1.5rem', marginBottom: 0.5 }}>
                  {shortcut.icon}
                </Box>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    lineHeight: 1.2,
                  }}
                >
                  {shortcut.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card sx={{ ...eventCardStyles, marginBottom: 3 }}>
        <CardContent sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 2,
          '&:last-child': { paddingBottom: 2 }
        }}>
          {loadingData ? (
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
              <CircularProgress size={24} sx={{ color: theme.colors.primary }} />
              <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.875rem' }}>
                Carregando...
              </Typography>
            </Box>
          ) : nextEvent ? (
            <>
              <Box
                onClick={() => futureEventsCount > 1 ? navigate('/events') : navigate(`/events/${nextEvent.id}`)}
                sx={{ display: 'flex', alignItems: 'flex-start', flex: 1, maxWidth: '65%', cursor: 'pointer' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', mr: 1 }}>
                  <EventAvailable sx={{ color: theme.colors.primary, fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      color: theme.colors.text.primary,
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      marginBottom: 0.5,
                    }}
                  >
                    {nextEvent.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: theme.colors.text.secondary,
                      fontSize: '0.75rem',
                      marginBottom: 1,
                    }}
                  >
                    {new Date(nextEvent.date).toLocaleDateString('pt-BR', {
                      day: 'numeric',
                      month: 'long'
                    })} às {nextEvent.time}
                  </Typography>
                  <Box component="ul" sx={{
                    margin: 0,
                    paddingLeft: 2,
                    '& li': {
                      fontSize: '0.7rem',
                      color: theme.colors.text.primary,
                      fontWeight: 600,
                      marginBottom: 0.25,
                    }
                  }}>
                    <li>Convidados: {nextEvent.guests?.length || 0}</li>
                    <li>{nextEvent.location || 'Local não definido'}</li>
                  </Box>
                </Box>
              </Box>
              {futureEventsCount > 1 && (
                <Box
                  onClick={() => navigate('/events')}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: 1,
                    cursor: 'pointer',
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: `${theme.colors.primary}15`,
                      border: `1px solid ${theme.colors.primary}40`,
                      borderRadius: theme.radii.large,
                      px: 2,
                      py: 0.5,
                      '&:hover': { backgroundColor: `${theme.colors.primary}25` },
                    }}
                  >
                    <Typography sx={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: theme.colors.primary,
                    }}>
                      Ver mais {futureEventsCount - 1} evento{futureEventsCount > 2 ? 's' : ''}
                    </Typography>
                  </Box>
                </Box>
              )}
              <Box
                onClick={() => futureEventsCount > 1 ? navigate('/events') : navigate(`/events/${nextEvent.id}`)}
                sx={{
                  backgroundColor: theme.colors.background,
                  borderRadius: theme.radii.medium,
                  width: 64,
                  height: 64,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.colors.text.primary,
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: `${theme.colors.primary}10`,
                  }
                }}
              >
                <Typography sx={{ fontSize: '1.125rem', lineHeight: 1 }}>
                  {new Date(nextEvent.date).getDate()}
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', lineHeight: 1 }}>
                  {new Date(nextEvent.date).toLocaleDateString('pt-BR', { month: 'short' }).toUpperCase()}
                </Typography>
              </Box>
            </>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', py: 2 }}>
              <EventAvailable sx={{ color: theme.colors.text.secondary, fontSize: 48, mb: 1, opacity: 0.5 }} />
              <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.875rem', mb: 2 }}>
                Nenhum evento futuro
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                <Button
                  fullWidth
                  onClick={handleAddEvent}
                  sx={{
                    backgroundColor: theme.colors.secondary,
                    color: theme.colors.text.primary,
                    borderRadius: theme.radii.medium,
                    padding: 1.5,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    justifyContent: 'center',
                    '&:hover': {
                      backgroundColor: theme.colors.secondary,
                      opacity: 0.8,
                    },
                  }}
                >
                  <CalendarMonth sx={{ fontSize: '1rem' }} />
                  ADICIONAR
                  {eventsRemaining > 0 && eventsRemaining !== -1 && (
                    <Typography component="span" sx={{ fontSize: '0.65rem', opacity: 0.8 }}>
                      ({eventsRemaining})
                    </Typography>
                  )}
                </Button>
                <Button
                  fullWidth
                  onClick={() => navigate('/events')}
                  sx={{
                    backgroundColor: theme.colors.primary,
                    color: theme.colors.text.contrast,
                    borderRadius: theme.radii.medium,
                    padding: 1.5,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    justifyContent: 'center',
                    '&:hover': {
                      backgroundColor: theme.colors.primary,
                      opacity: 0.8,
                    },
                  }}
                >
                  <CalendarToday sx={{ fontSize: '1rem' }} />
                  VER EVENTOS
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {nextEvent && (
        <Box sx={{ display: 'flex', gap: 1, marginBottom: 3 }}>
          <Button
            fullWidth
            onClick={handleAddEvent}
            sx={{
              backgroundColor: theme.colors.secondary,
              color: theme.colors.text.primary,
              borderRadius: theme.radii.medium,
              padding: 1.5,
              fontSize: '0.75rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              justifyContent: 'center',
              '&:hover': {
                backgroundColor: theme.colors.secondary,
                opacity: 0.8,
              },
            }}
          >
            <CalendarMonth sx={{ fontSize: '1rem' }} />
            ADICIONAR
            {eventsRemaining > 0 && eventsRemaining !== -1 && (
              <Typography component="span" sx={{ fontSize: '0.65rem', opacity: 0.8 }}>
                ({eventsRemaining})
              </Typography>
            )}
          </Button>
          <Button
            fullWidth
            onClick={() => navigate('/events')}
            sx={{
              backgroundColor: theme.colors.primary,
              color: theme.colors.text.contrast,
              borderRadius: theme.radii.medium,
              padding: 1.5,
              fontSize: '0.75rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              justifyContent: 'center',
              '&:hover': {
                backgroundColor: theme.colors.primary,
                opacity: 0.8,
              },
            }}
          >
            <CalendarToday sx={{ fontSize: '1rem' }} />
            VER EVENTOS
          </Button>
        </Box>
      )}

      {(() => {
        try {
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          const upcomingTodos = todosForDate('all', false)
            .filter(todo => {
              const todoDate = new Date(todo.date);
              todoDate.setHours(0, 0, 0, 0);
              return todoDate >= today;
            })
            .slice(0, 5);

          if (upcomingTodos.length === 0) return null;

          return (
            <Card sx={{ ...eventCardStyles, marginBottom: 3 }}>
              <CardContent sx={{
                padding: 2,
                '&:last-child': { paddingBottom: 2 }
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ChecklistRtl sx={{ color: theme.colors.primary, fontSize: 24 }} />
                    <Typography
                      sx={{
                        color: theme.colors.text.primary,
                        fontWeight: 'bold',
                        fontSize: '0.875rem',
                      }}
                    >
                      PRÓXIMAS TAREFAS
                    </Typography>
                  </Box>
                  <Button
                    onClick={() => navigate('/todo-list')}
                    sx={{
                      minWidth: 'auto',
                      padding: '4px 12px',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: theme.colors.primary,
                      backgroundColor: 'transparent',
                      '&:hover': {
                        backgroundColor: `${theme.colors.primary}10`,
                      }
                    }}
                  >
                    VER TUDO
                  </Button>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {upcomingTodos.map((todo) => (
                    <Box
                      key={todo.id}
                      onClick={() => toggleTodo(todo.id, !todo.completed)}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        padding: '8px',
                        borderRadius: theme.radii.small,
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          backgroundColor: `${theme.colors.primary}08`,
                        }
                      }}
                    >
                      <CheckBoxOutlineBlank
                        sx={{
                          color: theme.colors.primary,
                          fontSize: 20,
                          flexShrink: 0,
                          mt: 0.2
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            color: theme.colors.text.primary,
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            lineHeight: 1.4,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {todo.title}
                        </Typography>
                        <Typography
                          sx={{
                            color: theme.colors.text.secondary,
                            fontSize: '0.65rem',
                            mt: 0.3
                          }}
                        >
                          {new Date(todo.date).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: 'short'
                          }).toUpperCase()}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                  {todosForDate('all', false).filter(todo => {
                    const todoDate = new Date(todo.date);
                    todoDate.setHours(0, 0, 0, 0);
                    return todoDate >= today;
                  }).length > 5 && (
                      <Typography
                        sx={{
                          color: theme.colors.primary,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          textAlign: 'center',
                          mt: 0.5
                        }}
                      >
                        +{todosForDate('all', false).filter(todo => {
                          const todoDate = new Date(todo.date);
                          todoDate.setHours(0, 0, 0, 0);
                          return todoDate >= today;
                        }).length - 5} outras tarefas
                      </Typography>
                    )}
                </Box>
              </CardContent>
            </Card>
          );
        } catch (error) {
          return null;
        }
      })()}

      {/* Favoritos */}
      <Typography
        sx={{
          color: theme.colors.text.primary,
          fontWeight: 'bold',
          fontSize: '0.75rem',
          marginBottom: 1.5,
        }}
      >
        DESTAQUES
      </Typography>

      <Grid container spacing={1.5} sx={{ mb: 4 }}>
        <Grid size={{ xs: 6, md: 6 }}>
          <Card
            onClick={() => featuredItem ? navigate(`/inventory/${featuredItem.id}`) : navigate('/inventory')}
            sx={{ ...wishlistCardStyles, position: 'relative' }}
          >
            <IconButton
              onClick={(e) => { e.stopPropagation(); setPickItemOpen(true); }}
              size="small"
              aria-label="Trocar item em destaque"
              sx={{ position: 'absolute', top: 6, right: 6, zIndex: 2, backgroundColor: 'rgba(255,255,255,0.85)', width: 26, height: 26, '&:hover': { backgroundColor: '#fff' } }}
            >
              <EditIcon sx={{ fontSize: '0.9rem', color: theme.colors.primary }} />
            </IconButton>
            {loadingData ? (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme.colors.card,
                }}
              >
                <CircularProgress size={32} sx={{ color: theme.colors.primary }} />
              </Box>
            ) : featuredItem ? (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  backgroundImage: featuredItem.photoUrl
                    ? `url(${featuredItem.photoUrl})`
                    : 'linear-gradient(135deg, #E1C4B5 0%, #D4A574 100%)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {!featuredItem.photoUrl && (
                  <Typography sx={{ fontSize: '3rem', opacity: 0.5 }}>📦</Typography>
                )}
                <Box sx={wishlistLabelStyles}>
                  <Typography
                    component="div"
                    sx={{
                      fontSize: '0.625rem',
                      fontWeight: 'bold',
                      color: theme.colors.text.primary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                      maxWidth: '100%',
                      minWidth: 0,
                    }}
                  >
                    <Star sx={{ color: '#d9a520', fontSize: '0.75rem', flexShrink: 0 }} />
                    <Box component="span" title={featuredItem.itemName} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                      {featuredItem.itemName}
                    </Box>
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme.colors.card,
                  gap: 1,
                }}
              >
                <Typography sx={{ fontSize: '2rem', opacity: 0.3 }}>📦</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: theme.colors.text.secondary }}>
                  Sem itens
                </Typography>
              </Box>
            )}
          </Card>
        </Grid>

        <Grid size={{ xs: 6, md: 6 }}>
          <Card
            onClick={() => featuredRecipe ? navigate(`/recipes/${featuredRecipe.id}`) : navigate('/recipes')}
            sx={{ ...wishlistCardStyles, position: 'relative' }}
          >
            <IconButton
              onClick={(e) => { e.stopPropagation(); setPickRecipeOpen(true); }}
              size="small"
              aria-label="Trocar receita em destaque"
              sx={{ position: 'absolute', top: 6, right: 6, zIndex: 2, backgroundColor: 'rgba(255,255,255,0.85)', width: 26, height: 26, '&:hover': { backgroundColor: '#fff' } }}
            >
              <EditIcon sx={{ fontSize: '0.9rem', color: theme.colors.primary }} />
            </IconButton>
            {loadingData ? (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme.colors.card,
                }}
              >
                <CircularProgress size={32} sx={{ color: theme.colors.primary }} />
              </Box>
            ) : featuredRecipe ? (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  backgroundImage: featuredRecipe.imageUrl
                    ? `url(${featuredRecipe.imageUrl})`
                    : 'linear-gradient(135deg, #F4E4C1 0%, #E8C896 100%)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {!featuredRecipe.imageUrl && (
                  <Typography sx={{ fontSize: '3rem', opacity: 0.5 }}>🍽️</Typography>
                )}
                <Box sx={wishlistLabelStyles}>
                  <Typography
                    component="div"
                    sx={{
                      fontSize: '0.625rem',
                      fontWeight: 'bold',
                      color: theme.colors.text.primary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                      maxWidth: '100%',
                      minWidth: 0,
                    }}
                  >
                    <Star sx={{ color: '#d9a520', fontSize: '0.75rem', flexShrink: 0 }} />
                    <Box component="span" title={featuredRecipe.title} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                      {featuredRecipe.title}
                    </Box>
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme.colors.card,
                  gap: 1,
                }}
              >
                <Typography sx={{ fontSize: '2rem', opacity: 0.3 }}>🍽️</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: theme.colors.text.secondary }}>
                  Sem receitas
                </Typography>
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* Prompt de upgrade apenas no plano FREE quando restam 0 eventos */}
      {plan === 'free' && eventsRemaining === 0 && (
        <Box sx={{ mb: 2 }}>
          <UpgradePrompt
            feature="eventos"
            currentPlan={plan}
            onUpgrade={handleUpgrade}
          />
        </Box>
      )}

      {/* Escolher item do acervo em destaque */}
      <Dialog open={pickItemOpen} onClose={() => setPickItemOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Item em destaque</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <ListItem onClick={() => { saveFeatured('featuredInventoryId', null); setPickItemOpen(false); }} sx={{ cursor: 'pointer', backgroundColor: !featuredItemId ? `${theme.colors.primary}12` : 'transparent' }}>
            <ListItemText primary="Automático (mais usado)" primaryTypographyProps={{ fontWeight: !featuredItemId ? 700 : 500 }} />
          </ListItem>
          {inventoryItems.map((it: any) => (
            <ListItem key={it.id} onClick={() => { saveFeatured('featuredInventoryId', it.id); setPickItemOpen(false); }} sx={{ cursor: 'pointer', backgroundColor: featuredItemId === it.id ? `${theme.colors.primary}12` : 'transparent' }}>
              <ListItemText primary={it.itemName} primaryTypographyProps={{ fontWeight: featuredItemId === it.id ? 700 : 500 }} />
            </ListItem>
          ))}
          {inventoryItems.length === 0 && (
            <ListItem><ListItemText secondary="Nenhum item no acervo." /></ListItem>
          )}
        </DialogContent>
      </Dialog>

      {/* Escolher receita em destaque */}
      <Dialog open={pickRecipeOpen} onClose={() => setPickRecipeOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Receita em destaque</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <ListItem onClick={() => { saveFeatured('featuredRecipeId', null); setPickRecipeOpen(false); }} sx={{ cursor: 'pointer', backgroundColor: !featuredRecipeId ? `${theme.colors.primary}12` : 'transparent' }}>
            <ListItemText primary="Automático (mais usada)" primaryTypographyProps={{ fontWeight: !featuredRecipeId ? 700 : 500 }} />
          </ListItem>
          {allRecipes.map((r) => (
            <ListItem key={r.id} onClick={() => { saveFeatured('featuredRecipeId', r.id); setPickRecipeOpen(false); }} sx={{ cursor: 'pointer', backgroundColor: featuredRecipeId === r.id ? `${theme.colors.primary}12` : 'transparent' }}>
              <ListItemText primary={r.title} primaryTypographyProps={{ fontWeight: featuredRecipeId === r.id ? 700 : 500 }} />
            </ListItem>
          ))}
          {allRecipes.length === 0 && (
            <ListItem><ListItemText secondary="Nenhuma receita cadastrada." /></ListItem>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog completo de eventos */}
      <EventsDialog
        open={showEventsDialog}
        onClose={() => setShowEventsDialog(false)}
        onEventCreated={handleEventCreated}
      />

      {/* Dialog de limite atingido */}
      <LimitReachedDialog
        open={limitDialog.open}
        feature="eventos"
        onClose={() => setLimitDialog({ open: false, message: '' })}
        onUpgrade={handleUpgrade}
        currentPlan={plan}
      />

      {/* Snackbar para mensagens */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Trial Offer Dialog */}
      <TrialOfferDialog
        open={showTrialOffer}
        onAccept={handleAcceptTrial}
        onDecline={handleDeclineTrial}
      />

      {/* Bottom Navigation */}
      <BottomNavigation />
    </Box>
  );
};
